import React, { useEffect, useState, useContext } from 'react';
import { Outlet, useParams, useLocation, useNavigate } from 'react-router-dom';
import Banner from './Banner';
import Nav from './Nav';
import LoadingSpinner from './LoadingSpinner';
import axios from 'axios';
import AuthContext from './AuthContext'; // Import AuthContext for user info
import './Layout.css'; // Import Layout.css for styling

const apiClient = axios.create({
  baseURL: process.env.REACT_APP_PROXY_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

const Layout = () => {
  const { customerId, taxid, taxyear } = useParams();
  const location = useLocation();
  const [dataClient, setDataClient] = useState(null);
  const [dataProperty, setDataProperty] = useState(null);
  const [headerLoading, setHeaderLoading] = useState(true);
  const [error, setError] = useState(null);  
  const navigate = useNavigate();

  const { user } = useContext(AuthContext); // Get logged-in user info
  const isNotFoundPage = location.pathname.includes('/not-found');

  useEffect(() => {
    
    const fetchData = async () => {
      try {
        if (customerId) {
          // Always persist the last customerId as a fallback
          localStorage.setItem('customer_number', customerId);

          const responseClient = await apiClient.get(`/api/customer/${customerId}`);
          if (responseClient.data && responseClient.data.active === "Yes") {
            setDataClient(responseClient.data);
            // persist the actual customer_number from API if it differs
            localStorage.setItem('customer_number', responseClient.data.customer_number);
          } else {
            const fallbackId = localStorage.getItem('customer_number') || 'unknown';
            setDataClient({ customer_number: fallbackId, products: '', name: '' });
            navigate(`/customer/${fallbackId}/not-found`);
          }
        }

        if (customerId && taxid && taxyear) {
          // Fetch property data
          const responseProperty = await apiClient.get(
            `/api/property/${customerId}/${taxid}/${taxyear}`
          );
          setDataProperty(responseProperty.data);
        }

        setHeaderLoading(false);
      } catch (error) {
        setError(error);
        setHeaderLoading(false);
      }
    };

    fetchData();
  }, [customerId, taxid, taxyear]);

  if (headerLoading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return <div>Error: {error.message}</div>;
  }

  const titleGraphic = dataClient ? dataClient.title_graphic : '';

  // Function to determine page name based on the current pathname
  const getPageName = (pathname) => {
    //TAX SEARCH
    if (pathname.endsWith('/taxSearch')) {
      return 'Tax Search';
    } else if (pathname.endsWith('/taxSearchResults')) {
      return 'Tax Search Results';
    } else if (pathname.includes('/taxStatus')) {
      return 'Tax Status Detail';
    } else if (pathname.includes('/history')) {
      return 'Tax History Detail';
    } else if (pathname.includes('/propertyDetail')) {
      return 'Tax Property Detail';
    } else if (pathname.includes('/taxDetail')) {
      return 'Tax Detail';

    // TAX PAYMENTS
    } else if (pathname.includes('/propertyPayments/')) {
      return 'Payments Status';
    } else if (pathname.endsWith('/paymentsSearch')) {
      return 'Payments Search';
    } else if (pathname.endsWith('/paymentsSearchResults')) {
      return 'Payments Search Results';

    // PROPERTY
    } else if (pathname.endsWith('/propertySearch')) {
      return 'Search Results';
    } else if (pathname.startsWith('/property/')) {
      return 'Property Information';

    //CLIENT AND USER
    } else if (pathname.endsWith('client/list')) {
      return 'Client List';
    } else if (pathname.endsWith('user/list')) {
      return 'User List';
    } else if (pathname.endsWith('client/manage')) {
      return 'Edit Client';    
    } else if (pathname.endsWith('client/manage/new')) {
      return 'New Client';
    } else if (pathname.endsWith('user/manage/new')) {
      return 'New User';
    } else if (pathname.includes('user/manage')) {
      return 'Edit User';

    //LOGIN
    } else if (pathname.includes('/login')) {
      return 'CUSTOMER LOGIN';

    //OTHER
    } else if (pathname.includes('/TaxRoll/')) {
      return 'Tax Roll';
    } else if (pathname.startsWith('/propertyDetail/')) {
      return 'Property Detail';
    } else if (pathname.startsWith('/taxDetail/')) {
      return 'Tax Detail';
    } else if (pathname.startsWith('/history/')) {
      return 'History';
    } else if (pathname.startsWith('/billImage/')) {
      return 'Bill Image';
    } else {
      return '';
    }
  };

  const pageName = getPageName(location.pathname);

  return (
    <div className="bodyMain">
      <div className="mainContainer">
        <div className="wrapper page">
          {headerLoading ? (
            <LoadingSpinner />
          ) : (
            !isNotFoundPage && <Banner titleGraphic={titleGraphic} />
          )}
          {!headerLoading && (
            <div id="contentMain">
              <div className="contentContainer">
                {!isNotFoundPage && (
                <div className="navContainer">
                  <Nav dataClient={dataClient ?? { customer_number: localStorage.getItem('customer_number') || 'unknown', products: '', name: '' }} />
                </div>
                )}
                {/* Pass dataClient to child components via Outlet context */}
                <div className="contentBody">
                  <div id="contentNavBar">
                    <div className="content content-bar">
                      {!isNotFoundPage && (
                      <div className="left-content">
                        {(dataClient && dataClient.name) || 'Unknown Customer'} | <span className="bold">{pageName}</span>
                      </div>
                      )}
                      {user && (
                        <div className="right-content">
                          <span className="normal">{user.sub}</span> ({user.role_name})
                        </div>
                      )}
                    </div>
                  </div>
                  <div id="content">
                    <Outlet context={{ dataClient, dataProperty }} />
                  </div>
                  <div className="footerContent">
                    &copy; {new Date().getFullYear()} Black Mountain Software. All Rights Reserved.
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Layout;